/**
 * ProviderSelectionPanel — ops list of interested candidates + Select CTA (D-015).
 */

import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
} from "@mui/material";
import { Handshake } from "@mui/icons-material";
import {
  getOpportunityByOrderId,
  getCandidates,
  selectProvider,
} from "../services/providerOpportunityService.js";
import { logError } from "@/shared/utils/logger.js";

const SELECTABLE_STATUSES = new Set(["open", "selection_pending"]);

const formatMoney = (amount, currency = "ZAR") => {
  const num = Number(amount);
  if (!Number.isFinite(num)) return "—";
  const prefix = currency === "ZAR" || !currency ? "R" : `${currency} `;
  return `${prefix}${num.toLocaleString("en-ZA", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const payoutFromCandidate = (candidate) => {
  const payout = candidate?.termsSnapshot?.payout;
  if (!payout || typeof payout !== "object") return null;
  return payout;
};

/**
 * @param {object} props
 * @param {string} props.orderId
 * @param {object} [props.order]
 * @param {() => void} [props.onAssigned]
 */
const ProviderSelectionPanel = ({ orderId, order, onAssigned }) => {
  const [loading, setLoading] = useState(true);
  const [selectingId, setSelectingId] = useState(null);
  const [error, setError] = useState(null);
  const [opportunity, setOpportunity] = useState(null);
  const [candidates, setCandidates] = useState([]);

  const load = useCallback(async () => {
    if (!orderId) {
      setLoading(false);
      setOpportunity(null);
      setCandidates([]);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const opp = await getOpportunityByOrderId(orderId);
      setOpportunity(opp);
      if (!opp) {
        setCandidates([]);
        return;
      }
      if (SELECTABLE_STATUSES.has(opp.status)) {
        const rows = await getCandidates(opp.id);
        setCandidates(rows);
      } else {
        setCandidates([]);
      }
    } catch (err) {
      logError("ProviderSelectionPanel load failed", {
        orderId,
        error: err?.message,
      });
      setError(err?.message || "Failed to load provider opportunity");
      setOpportunity(null);
      setCandidates([]);
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSelect = async (candidate) => {
    if (!opportunity?.id || !candidate) return;
    setSelectingId(candidate.invitationId || candidate.partnerId);
    setError(null);
    try {
      await selectProvider(opportunity.id, {
        invitationId: candidate.invitationId,
        partnerId: candidate.partnerId,
      });
      await load();
      if (typeof onAssigned === "function") {
        onAssigned();
      }
    } catch (err) {
      setError(err?.message || "Failed to select provider");
    } finally {
      setSelectingId(null);
    }
  };

  if (loading) {
    return (
      <Card sx={{ mb: 3 }} data-testid="provider-selection-panel-loading">
        <CardContent>
          <Box display="flex" alignItems="center" gap={1.5}>
            <CircularProgress size={22} />
            <Typography variant="body2" color="text.secondary">
              Loading provider opportunity…
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (!opportunity) {
    return null;
  }

  const assignedTeam =
    order?.booking?.assignedTeam || order?.assignedTeam || null;
  const isAssigned = opportunity.status === "assigned";
  const isSelectable = SELECTABLE_STATUSES.has(opportunity.status);

  if (!isSelectable && !isAssigned) {
    return null;
  }

  return (
    <Card sx={{ mb: 3 }} data-testid="provider-selection-panel">
      <CardContent>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          gap={2}
          mb={2}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Handshake color="primary" />
            <Box>
              <Typography variant="h6">Provider selection</Typography>
              <Typography variant="body2" color="text.secondary">
                {opportunity.reference || opportunity.id} ·{" "}
                {String(opportunity.status || "").replaceAll("_", " ")}
              </Typography>
            </Box>
          </Stack>
          <Chip
            size="small"
            label={String(opportunity.status || "unknown")
              .replaceAll("_", " ")
              .toUpperCase()}
            color={isAssigned ? "success" : "primary"}
            variant={isAssigned ? "filled" : "outlined"}
          />
        </Box>

        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}

        {isAssigned ? (
          <Box data-testid="provider-selection-assigned">
            <Divider sx={{ mb: 2 }} />
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Assigned partner
            </Typography>
            <Typography variant="body1" fontWeight={600}>
              {assignedTeam?.partnerName ||
                assignedTeam?.partnerId ||
                "Partner assigned"}
            </Typography>
            {assignedTeam?.partnerId ? (
              <Typography variant="body2" color="text.secondary">
                {assignedTeam.partnerId}
              </Typography>
            ) : null}
          </Box>
        ) : null}

        {isSelectable ? (
          <>
            <Divider sx={{ mb: 2 }} />
            {candidates.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No interested providers yet. Invite providers, then return here
                to select.
              </Typography>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small" aria-label="Provider candidates">
                  <TableHead>
                    <TableRow>
                      <TableCell>Provider</TableCell>
                      <TableCell>Payout</TableCell>
                      <TableCell>Eligibility</TableCell>
                      <TableCell align="right">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {candidates.map((candidate) => {
                      const eligible =
                        candidate?.eligibility?.eligible !== false;
                      const payout = payoutFromCandidate(candidate);
                      const rowKey =
                        candidate.invitationId || candidate.partnerId;
                      const selecting = selectingId === rowKey;
                      return (
                        <TableRow key={rowKey} hover>
                          <TableCell>
                            <Typography variant="body2" fontWeight={600}>
                              {candidate.partnerName ||
                                candidate.partnerId ||
                                "Provider"}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {candidate.partnerId}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {payout
                              ? formatMoney(payout.amount, payout.currency)
                              : "—"}
                          </TableCell>
                          <TableCell>
                            {eligible ? (
                              <Chip size="small" label="Eligible" color="success" />
                            ) : (
                              <Chip
                                size="small"
                                label={
                                  (candidate?.eligibility?.reasons || [])
                                    .slice(0, 2)
                                    .join(", ") || "Ineligible"
                                }
                                color="default"
                              />
                            )}
                          </TableCell>
                          <TableCell align="right">
                            <Button
                              size="small"
                              variant="contained"
                              disabled={!eligible || !!selectingId}
                              onClick={() => handleSelect(candidate)}
                              sx={{ textTransform: "none" }}
                            >
                              {selecting ? (
                                <CircularProgress size={16} color="inherit" />
                              ) : (
                                "Select"
                              )}
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        ) : null}
      </CardContent>
    </Card>
  );
};

export default ProviderSelectionPanel;
